<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useTranslation } from 'i18next-vue'
import FileUpload, { type FileUploadSelectEvent } from 'primevue/fileupload'
import Button from 'primevue/button'
import { uploadFile } from '@/api/uploadApi'
import { useToastStore } from '@/stores/toast.store'
import { STRAPI_URL } from '@/api/http'
import type { AxiosError } from 'axios'
import type { UploadedMediaValue } from '@/types/global'
import {
    type FileCategory,
    formatAcceptedFileTypes,
    formatFileSize,
    getDefaultUploadMaxFileSizeInBytes,
    getUploadDocumentAcceptedMimeTypes,
    getUploadImageAcceptedMimeTypes,
    getUploadVideoAcceptedMimeTypes,
    getUploadVideoAndImageAcceptedMimeTypes,
    mediaUploadComponentGetMediaType,
} from '@/utils/media-helper'
import type { UploadedItem } from '@/types/formats.ts'

type StrapiErrorResponse = {
    error?: {
        message?: string
    }
}

// PrimeVue extends File with objectURL property
interface FileWithObjectURL extends File {
    objectURL?: string
}

export type MediaValue = string | number | UploadedItem | UploadedMediaValue | null | undefined

interface Props {
    disabled?: boolean
    accept?: string
    maxFileSize?: number
    multiple?: boolean
    mediaType?: FileCategory
    i18nKey?: string
    testIdPrefix?: string
    showLabel?: boolean
    label?: string
    storeFullObject?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    accept: undefined,
    maxFileSize: getDefaultUploadMaxFileSizeInBytes(),
    // Multiple file upload not yet implemented - always use single selection
    multiple: false as const,
    mediaType: 'unknown',
    i18nKey: undefined,
    testIdPrefix: 'media-upload',
    showLabel: true,
    label: undefined,
    storeFullObject: false,
})

const value = defineModel<MediaValue>({ required: true })

const { t } = useTranslation()
const { showToast } = useToastStore()

const uploadedFiles = ref<File[]>([])
const isUploading = ref(false)

const acceptComputed = computed(() => {
    if (props.accept) return props.accept
    switch (props.mediaType) {
        case 'image':
            return getUploadImageAcceptedMimeTypes()
        case 'video':
            return getUploadVideoAcceptedMimeTypes()
        case 'videoandimage':
            return getUploadVideoAndImageAcceptedMimeTypes()
        case 'document':
            return getUploadDocumentAcceptedMimeTypes()
        default:
            return '*/*'
    }
})

// Watch for external changes to v-model and clear uploadedFiles
// This handles the case when user navigates back after saving
watch(
    () => value.value,
    (newValue, oldValue) => {
        // Clear uploaded files when value changes externally (e.g., after navigation)
        // but not when it's being set by our upload handler
        if (newValue !== oldValue && !isUploading.value) {
            uploadedFiles.value = []
        }
    }
)

const existingFileUrl = computed(() => {
    if (!value.value) return null

    // If it's a number (Strapi ID), we can't display it without fetching from API
    // This is a limitation - the component needs the URL, not just the ID
    if (typeof value.value === 'number') {
        return null
    }

    if (typeof value.value === 'string') {
        if (value.value.startsWith('http')) return value.value
        if (value.value.startsWith('/')) return `${STRAPI_URL}${value.value}`
        return null
    }

    if (typeof value.value === 'object' && 'url' in value.value) {
        const url = value.value.url
        if (url.startsWith('http')) return url
        return `${STRAPI_URL}${url}`
    }

    return null
})

// Detect the actual media type from the stored file
const actualMediaType = computed(() => {
    return mediaUploadComponentGetMediaType(value.value, props.mediaType)
})

const currentFileTranslationKeys: Record<FileCategory, string> = {
    image: 'ui.upload.image.currentFile',
    video: 'ui.upload.video.currentFile',
    videoandimage: 'ui.upload.videoandimage.currentFile',
    document: 'ui.upload.document.currentFile',
    unknown: 'ui.upload.file.currentFile',
}

const currentFileLabel = computed(() => {
    if (props.i18nKey) {
        return t(`${props.i18nKey}.currentFile`, {
            defaultValue: t('ui.upload.currentFile'),
        })
    }

    return t(currentFileTranslationKeys[actualMediaType.value], {
        defaultValue: t('ui.upload.currentFile'),
    })
})

// Computed i18n key with fallback, considering actual media type
const i18nKeyComputed = computed(() => {
    if (props.i18nKey) return props.i18nKey

    // Use actual media type for better i18n (e.g., if video accepts images, show "current image" for images)
    const typeToUse = actualMediaType.value

    switch (typeToUse) {
        case 'image':
            return 'ui.upload.image'
        case 'video':
            return 'ui.upload.video'
        case 'videoandimage':
            return 'ui.upload.videoandimage'
        case 'document':
            return 'ui.upload.document'
        default:
            return 'ui.upload.file'
    }
})

const maxFileSizeFormatted = computed(() => {
    const sizeInMB = props.maxFileSize / (1024 * 1024)
    return `${sizeInMB} MB`
})

// Get icon based on media type
const getIcon = computed(() => {
    switch (props.mediaType) {
        case 'image':
            return 'pi pi-image'
        case 'videoandimage':
            return 'pi pi-camera'
        case 'video':
            return 'pi pi-video'
        case 'document':
            return 'pi pi-file'
        default:
            return 'pi pi-cloud-upload'
    }
})

const isImageFile = (file: File): boolean => {
    return file.type.startsWith('image/')
}

const isVideoFile = (file: File): boolean => {
    return file.type.startsWith('video/')
}

const isActualMediaTypeImage = computed(() => {
    return actualMediaType?.value === 'image'
})

const isActualMediaTypeVideo = computed(() => {
    return actualMediaType?.value === 'video'
})

const fileUploadPtConfig = computed(() => {
    return {
        input: {
            id: `${props.testIdPrefix}-input`,
            'data-testid': `${props.testIdPrefix}-input`,
            'aria-label': props.showLabel
                ? undefined
                : props.label ||
                  t(`${i18nKeyComputed.value}.uploadLabel`, {
                      defaultValue: t('ui.upload.uploadLabel'),
                  }),
        },
    }
})

const onFileSelect = async (event: FileUploadSelectEvent) => {
    const files = event.files
    if (!files || files.length === 0) return

    uploadedFiles.value = files
    isUploading.value = true

    try {
        // For now, handle single file upload
        const file = files[0]
        const uploadedMedia = await uploadFile(file)

        // Store either full object or just ID based on prop
        if (props.storeFullObject) {
            value.value = {
                id: uploadedMedia.id,
                documentId: uploadedMedia.documentId,
                width: uploadedMedia.width || 0,
                height: uploadedMedia.height || 0,
                url: uploadedMedia.url,
                formats: uploadedMedia.formats || {},
            }
        } else {
            value.value = uploadedMedia.id
        }

        showToast({
            severity: 'success',
            summary: t('ui.common.success'),
            detail: t(`${i18nKeyComputed.value}.uploadSuccess`, {
                defaultValue: t('ui.upload.uploadSuccess'),
            }),
        })
    } catch (error: unknown) {
        uploadedFiles.value = []
        value.value = null

        let errorMessage = t(`${i18nKeyComputed.value}.uploadError`, {
            defaultValue: t('ui.upload.uploadError'),
        })

        if (isAxiosError<StrapiErrorResponse>(error)) {
            errorMessage = error.response?.data?.error?.message ?? error.message ?? errorMessage
        } else if (error instanceof Error) {
            errorMessage = error.message
        }

        showToast({
            severity: 'error',
            summary: t('ui.common.error'),
            detail: errorMessage,
        })
    } finally {
        isUploading.value = false
    }
}

const onFileRemove = () => {
    uploadedFiles.value = []
    value.value = null
}

function isAxiosError<T = unknown>(error: unknown): error is AxiosError<T> {
    return typeof error === 'object' && error !== null && 'isAxiosError' in error
}
</script>

<template>
    <div class="space-y-4">
        <label
            v-if="showLabel"
            :for="`${testIdPrefix}-input`"
            class="text-neutral-600 flex items-center mb-1"
        >
            {{
                label ||
                t(`${i18nKeyComputed}.uploadLabel`, { defaultValue: t('ui.upload.uploadLabel') })
            }}
        </label>

        <FileUpload
            name="files"
            :accept="acceptComputed"
            :max-file-size="maxFileSize"
            :disabled="disabled"
            custom-upload
            :multiple="multiple"
            :invalid-file-type-message="
                t('ui.upload.invalidFileTypeMessage', {
                    fileTypes: formatAcceptedFileTypes(acceptComputed),
                })
            "
            :invalid-file-size-message="
                t('ui.upload.invalidFileSizeMessage', { maxSize: formatFileSize(maxFileSize) })
            "
            :data-testid="`${testIdPrefix}-upload`"
            :pt="fileUploadPtConfig"
            @select="onFileSelect"
            @remove="onFileRemove"
            @clear="onFileRemove"
        >
            <template #header="{ chooseCallback }">
                <div class="flex gap-2">
                    <Button
                        :icon="getIcon"
                        :label="
                            t(`${i18nKeyComputed}.chooseFile`, {
                                defaultValue: t('ui.upload.chooseFile'),
                            })
                        "
                        :data-testid="`${testIdPrefix}-choose`"
                        rounded
                        severity="secondary"
                        @click="chooseCallback()"
                    />
                </div>
            </template>

            <template #empty>
                <div
                    v-if="existingFileUrl && uploadedFiles.length === 0"
                    class="flex flex-col gap-4 pt-4"
                >
                    <div
                        class="flex items-center gap-4 p-4 border border-surface-200 rounded-lg bg-surface-50"
                    >
                        <div class="shrink-0">
                            <img
                                v-if="isActualMediaTypeImage"
                                :src="existingFileUrl"
                                :alt="currentFileLabel"
                                class="w-16 h-16 object-contain rounded"
                            />
                            <video
                                v-else-if="isActualMediaTypeVideo"
                                :src="existingFileUrl"
                                class="w-16 h-16 object-contain rounded"
                                muted
                                :aria-label="currentFileLabel"
                            />
                            <i
                                v-else
                                :class="getIcon"
                                class="text-4xl text-neutral-600"
                                aria-hidden="true"
                            />
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="text-sm font-medium text-surface-900">
                                {{ currentFileLabel }}
                            </p>
                        </div>
                        <Button
                            icon="pi pi-times"
                            :aria-label="t('ui.common.remove')"
                            severity="danger"
                            text
                            rounded
                            :disabled="disabled"
                            :data-testid="`${testIdPrefix}-remove-existing`"
                            @click="onFileRemove"
                        />
                    </div>
                </div>

                <div v-else class="flex items-center justify-center flex-col py-12 px-6">
                    <i :class="getIcon" class="text-6xl text-neutral-600 mb-4" aria-hidden="true" />
                    <p class="text-sm text-secondary mb-1">
                        {{
                            t(`${i18nKeyComputed}.dragDrop`, {
                                defaultValue: t('ui.upload.dragDrop'),
                            })
                        }}
                    </p>
                    <p v-if="accept" class="text-xs text-neutral-600">
                        {{ t('ui.upload.fileTypes') }}:
                        {{ formatAcceptedFileTypes(accept) }}
                    </p>
                </div>
            </template>

            <template #content="{ files, removeFileCallback }">
                <div v-if="files.length > 0" class="flex flex-col gap-4 pt-4">
                    <div
                        v-for="(file, index) of files"
                        :key="file.name + file.type + file.size"
                        class="flex items-center gap-4 p-4 border border-surface-200 rounded-lg bg-surface-50"
                    >
                        <div class="shrink-0">
                            <img
                                v-if="isImageFile(file) && (file as FileWithObjectURL).objectURL"
                                :src="(file as FileWithObjectURL).objectURL"
                                :alt="file.name"
                                class="w-16 h-16 object-contain rounded"
                            />
                            <video
                                v-else-if="
                                    isVideoFile(file) && (file as FileWithObjectURL).objectURL
                                "
                                :src="(file as FileWithObjectURL).objectURL"
                                class="w-16 h-16 object-contain rounded"
                                muted
                                :aria-label="file.name"
                            />
                            <i
                                v-else
                                :class="getIcon"
                                class="text-4xl text-neutral-600"
                                aria-hidden="true"
                            />
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="paragraph-sm font-medium text-surface-900 truncate">
                                {{ file.name }}
                            </p>
                            <p class="paragraph-sm text-neutral-600">
                                {{ (file.size / 1024).toFixed(0) }} KB
                            </p>
                        </div>
                        <Button
                            icon="pi pi-times"
                            :aria-label="t('ui.common.remove')"
                            severity="danger"
                            text
                            rounded
                            :disabled="disabled"
                            :data-testid="`${testIdPrefix}-remove`"
                            @click="removeFileCallback(index)"
                        />
                    </div>
                </div>
            </template>
        </FileUpload>

        <p class="text-xs text-neutral-600">
            {{
                t(`${i18nKeyComputed}.maxSize`, {
                    size: maxFileSizeFormatted,
                    defaultValue: t('ui.upload.maxSize', { size: maxFileSizeFormatted }),
                })
            }}
        </p>
    </div>
</template>
